import React, { useState } from 'react';
import { Camera, MapPin, CheckCircle, XCircle, Upload } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'react-toastify';

const IssueVerificationModal = ({ issue, onClose, onVerified }) => {
  const [verifying, setVerifying] = useState(false);
  const [verificationType, setVerificationType] = useState('');
  const [comment, setComment] = useState('');
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [gettingLocation, setGettingLocation] = useState(false);

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhoto(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleVerify = async () => {
    if (!verificationType) {
      toast.error('Please select verification status');
      return;
    }

    setGettingLocation(true);

    // Get user's current location
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        setGettingLocation(false);
        setVerifying(true);

        try {
          const { latitude, longitude } = position.coords;

          // Upload photo if provided
          let photoUrl = null;
          if (photo) {
            const formData = new FormData();
            formData.append('file', photo);
            const uploadRes = await api.post('/posts/upload-image', formData);
            photoUrl = uploadRes.data.image_url;
          }

          // Submit verification
          await api.post(`/posts/${issue.id}/verify`, {
            verification_type: verificationType,
            latitude,
            longitude,
            comment: comment || undefined,
            photo_url: photoUrl
          });

          toast.success(`Issue marked as ${verificationType === 'fixed' ? 'Fixed' : 'Still an Issue'}! +20 points`);
          onVerified();
          onClose();
        } catch (error) {
          const errorMsg = error.response?.data?.detail || 'Failed to verify issue';
          toast.error(errorMsg);
        } finally {
          setVerifying(false);
        }
      },
      (error) => {
        setGettingLocation(false);
        toast.error('Please enable location to verify this issue');
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-[#111] border border-[#1a1a1a] rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-[#1a1a1a]">
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-display text-xl text-white">VERIFY ISSUE</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-white">✕</button>
          </div>
          <p className="text-sm text-gray-400">
            Go to the location and verify if this issue is fixed or still exists
          </p>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Issue Details */}
          <div className="bg-[#0a0a0a] rounded p-4">
            <p className="text-gray-300 mb-2">{issue.content}</p>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <MapPin className="w-4 h-4" />
              <span>{issue.address || issue.ward || 'Location not specified'}</span>
            </div>
          </div>

          {/* Verification Status */}
          <div>
            <label className="block text-sm font-mono text-gray-400 mb-3">STATUS AT LOCATION *</label>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setVerificationType('fixed')}
                className={`p-4 rounded border-2 transition-all ${
                  verificationType === 'fixed'
                    ? 'border-green-500 bg-green-500/10'
                    : 'border-[#1a1a1a] hover:border-green-500/50'
                }`}
              >
                <CheckCircle className={`w-8 h-8 mx-auto mb-2 ${
                  verificationType === 'fixed' ? 'text-green-500' : 'text-gray-600'
                }`} />
                <div className="text-sm font-mono text-white">FIXED</div>
                <div className="text-xs text-gray-500 mt-1">Issue resolved</div>
              </button>

              <button
                onClick={() => setVerificationType('still_issue')}
                className={`p-4 rounded border-2 transition-all ${
                  verificationType === 'still_issue'
                    ? 'border-red-500 bg-red-500/10'
                    : 'border-[#1a1a1a] hover:border-red-500/50'
                }`}
              >
                <XCircle className={`w-8 h-8 mx-auto mb-2 ${
                  verificationType === 'still_issue' ? 'text-red-500' : 'text-gray-600'
                }`} />
                <div className="text-sm font-mono text-white">STILL BROKEN</div>
                <div className="text-xs text-gray-500 mt-1">Issue persists</div>
              </button>
            </div>
          </div>

          {/* Photo Upload */}
          <div>
            <label className="block text-sm font-mono text-gray-400 mb-3">CURRENT PHOTO (Optional)</label>
            {photoPreview ? (
              <div className="relative">
                <img src={photoPreview} alt="Preview" className="w-full h-48 object-cover rounded" />
                <button
                  onClick={() => { setPhoto(null); setPhotoPreview(null); }}
                  className="absolute top-2 right-2 bg-black/80 text-white p-2 rounded-full hover:bg-black"
                >
                  ✕
                </button>
              </div>
            ) : (
              <label className="block border-2 border-dashed border-[#1a1a1a] rounded p-8 text-center hover:border-[#C5A52A]/50 cursor-pointer transition-colors">
                <Camera className="w-8 h-8 text-gray-600 mx-auto mb-2" />
                <div className="text-sm text-gray-500">Click to upload photo</div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoChange}
                  className="hidden"
                />
              </label>
            )}
          </div>

          {/* Comment */}
          <div>
            <label className="block text-sm font-mono text-gray-400 mb-2">NOTES (Optional)</label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Any additional details..."
              className="w-full bg-[#0a0a0a] border border-[#1a1a1a] rounded px-4 py-3 text-white placeholder-gray-600 focus:border-[#C5A52A] focus:outline-none"
              rows="3"
            />
          </div>

          {/* Reward Info */}
          <div className="bg-gradient-to-r from-[#C5A52A]/10 to-transparent border border-[#C5A52A]/30 rounded p-4">
            <div className="flex items-center gap-3">
              <div className="text-3xl">🏆</div>
              <div>
                <div className="font-mono text-[#C5A52A] font-bold">+20 POINTS</div>
                <div className="text-xs text-gray-400">Reward for verifying this issue</div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-[#1a1a1a] flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 bg-[#1a1a1a] hover:bg-[#2a2a2a] text-white rounded font-mono text-sm transition-colors"
          >
            CANCEL
          </button>
          <button
            onClick={handleVerify}
            disabled={!verificationType || verifying || gettingLocation}
            className="flex-1 px-4 py-3 bg-[#C5A52A] hover:bg-[#D4B93A] text-black rounded font-mono text-sm font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {gettingLocation ? (
              'GETTING LOCATION...'
            ) : verifying ? (
              'VERIFYING...'
            ) : (
              <>
                <MapPin className="w-4 h-4 inline mr-2" />
                VERIFY AT LOCATION
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default IssueVerificationModal;
